import React from 'react';
import { style } from 'typestyle';
import * as tabStyles from '../statistics-tabs.styles';
import * as styles from '../tabs/reporting-tab.styles';
import { type ReportingComparisonMode, type ReportingKpis } from '../models/dashboard.models';

export const comparisonLabelKey = (comparison: ReportingComparisonMode): string => {
  if (comparison === 'PreviousPeriod') {
    return 'reporting-delta-vs-previous-period';
  }
  if (comparison === 'SamePeriodLastYear') {
    return 'reporting-delta-vs-same-period-last-year';
  }
  return '';
};

interface DeltaProps {
  readonly current: number;
  readonly previous?: number;
  readonly comparison: ReportingComparisonMode;
  readonly suffix: string;
  readonly t: (key: string) => string;
}

export const KpiDelta = ({ current, previous, comparison, suffix, t }: DeltaProps): React.JSX.Element | null => {
  if (previous === undefined || comparison === 'None') {
    return null;
  }

  const delta = Math.round((current - previous) * 10) / 10;

  let arrow = '–';
  let cssClass = styles.deltaNeutral;
  if (delta > 0) {
    arrow = '▲';
    cssClass = styles.deltaPositive;
  } else if (delta < 0) {
    arrow = '▼';
    cssClass = styles.deltaNegative;
  }

  const label = t(comparisonLabelKey(comparison));

  return (
    <span className={style(cssClass)}>
      {arrow} {Math.abs(delta)}
      {suffix} {label}
    </span>
  );
};

/**
 * A KPI value, optionally followed by " / total" in a lighter style — e.g. "10 / 200" for the group
 * reservations out of all reservations. The accessible name spells it out ("10 of 200").
 */
const KpiValue = ({ value, total, t }: { value: string; total?: string; t: (key: string) => string }): React.JSX.Element => {
  if (total === undefined) {
    return <div className={style(tabStyles.kpiValue)}>{value}</div>;
  }
  return (
    <div className={style(tabStyles.kpiValue)} aria-label={`${value} ${t('reporting-kpi-of')} ${total}`}>
      {value}
      <span className={style(styles.kpiTotal)} aria-hidden="true">
        {' / '}
        {total}
      </span>
    </div>
  );
};

const percent = (value: number): string => `${value.toFixed(1)}%`;

interface ReportingKpiRowProps {
  readonly kpis: ReportingKpis;
  readonly comparisonKpis?: ReportingKpis;
  readonly comparison: ReportingComparisonMode;
  /** Shown above the row, so the overall and the group KPIs can be told apart. */
  readonly title?: string;
  /**
   * The all-reservations KPIs, for a row that is a part of them (the group row): the counts (reservations,
   * no-shows) are then shown as "part / total", e.g. "10 / 200". The percentages stay the row's own figure —
   * an overall percentage after a group one reads as a share, which it is not. The change against the
   * comparison period stays on the part.
   */
  readonly totals?: ReportingKpis;
  readonly t: (key: string) => string;
}

/**
 * The four reporting KPIs — average occupancy, reservations, no-shows and no-show rate — each with its
 * change against the comparison period. Used for all reservations and again for group reservations.
 */
export const ReportingKpiRow = ({ kpis, comparisonKpis, comparison, title, totals, t }: ReportingKpiRowProps): React.JSX.Element => (
  <section aria-label={title}>
    {title && <div className={style(styles.kpiSectionTitle)}>{title}</div>}
    <div className={style(tabStyles.kpiRow)}>
      <div className={style(tabStyles.kpiCard)}>
        <div className={style(tabStyles.kpiLabel)}>{t('reporting-kpi-avg-occupancy')}</div>
        <KpiValue value={percent(kpis.averageOccupancyPercentage)} t={t} />
        <KpiDelta
          current={kpis.averageOccupancyPercentage}
          previous={comparisonKpis?.averageOccupancyPercentage}
          comparison={comparison}
          suffix="pts"
          t={t}
        />
      </div>
      <div className={style(tabStyles.kpiCard)}>
        <div className={style(tabStyles.kpiLabel)}>{t('reporting-kpi-reservations')}</div>
        <KpiValue value={String(kpis.reservations)} total={totals && String(totals.reservations)} t={t} />
        <KpiDelta current={kpis.reservations} previous={comparisonKpis?.reservations} comparison={comparison} suffix="" t={t} />
      </div>
      <div className={style(tabStyles.kpiCard)}>
        <div className={style(tabStyles.kpiLabel)}>{t('reporting-kpi-no-shows')}</div>
        <KpiValue value={String(kpis.noShows)} total={totals && String(totals.noShows)} t={t} />
        <KpiDelta current={kpis.noShows} previous={comparisonKpis?.noShows} comparison={comparison} suffix="" t={t} />
      </div>
      <div className={style(tabStyles.kpiCard)}>
        <div className={style(tabStyles.kpiLabel)}>{t('reporting-kpi-no-show-rate')}</div>
        <KpiValue value={percent(kpis.noShowRatePercentage)} t={t} />
        <KpiDelta
          current={kpis.noShowRatePercentage}
          previous={comparisonKpis?.noShowRatePercentage}
          comparison={comparison}
          suffix="pts"
          t={t}
        />
      </div>
    </div>
  </section>
);
